<script setup lang="ts">
import { computed, ref } from 'vue';

type ClientType = 'individual' | 'legal';
type Subject = 'car' | 'commercial' | 'spec';

/**
 * Калькулятор лизинга (главная страница).
 * Расчёт аннуитетного платежа с учётом типа клиента и предмета лизинга.
 */
const clientType = ref<ClientType>('individual');
const subject = ref<Subject>('car');
const cost = ref(30000);
const advance = ref(10);
const buyout = ref(1);
const term = ref(36);

// Годовые ставки с учётом типа клиента: rates[тип клиента][предмет лизинга]
const rates: Record<ClientType, Record<Subject, number>> = {
  individual: { car: 0.135, commercial: 0.142, spec: 0.148 },
  legal: { car: 0.129, commercial: 0.136, spec: 0.142 },
};

const subjectOptions: { value: Subject; label: string }[] = [
  { value: 'car', label: 'Легковой автомобиль' },
  { value: 'commercial', label: 'Коммерческий транспорт' },
  { value: 'spec', label: 'Спецтехника' },
];

function fmt(n: number): string {
  return `${Math.round(n).toLocaleString('ru-RU')} BYN`;
}

const advanceAmount = computed(() => cost.value * (advance.value / 100));
const buyoutAmount = computed(() => cost.value * (buyout.value / 100));

const monthlyPayment = computed(() => {
  const rate = rates[clientType.value][subject.value];
  const financed = cost.value - advanceAmount.value; // сумма финансирования после аванса
  const residual = buyoutAmount.value; // выкупная стоимость
  const monthlyRate = rate / 12;
  const n = term.value;
  // Аннуитетный платёж с учётом выкупной стоимости
  return (
    (financed - residual) *
      ((monthlyRate * Math.pow(1 + monthlyRate, n)) /
        (Math.pow(1 + monthlyRate, n) - 1)) +
    residual * monthlyRate
  );
});
</script>

<template>
  <section id="calculator">
    <div class="container-narrow">
      <span class="eyebrow">Онлайн-расчёт</span>
      <h2 class="section-title">Калькулятор лизинга</h2>
      <p class="section-lead">
        Предварительный расчёт ежемесячного платежа. Итоговые условия зависят от
        индивидуальной оценки заявки и не являются публичной офертой.
      </p>

      <div class="card card-lg">
        <div class="grid grid-form">
          <div class="field">
            <label for="clientType">Тип клиента</label>
            <select id="clientType" v-model="clientType">
              <option value="individual">Физическое лицо</option>
              <option value="legal">Юридическое лицо / ИП</option>
            </select>
          </div>
          <div class="field">
            <label for="subject">Предмет лизинга</label>
            <select id="subject" v-model="subject">
              <option v-for="opt in subjectOptions" :key="opt.value" :value="opt.value">
                {{ opt.label }}
              </option>
            </select>
          </div>
        </div>

        <div class="field">
          <label for="cost">Стоимость предмета лизинга: <strong>{{ fmt(cost) }}</strong></label>
          <input id="cost" v-model.number="cost" type="range" min="10000" max="300000" step="1000" />
        </div>

        <div class="field">
          <label for="advance">Авансовый платёж: <strong>{{ advance }}%</strong></label>
          <input id="advance" v-model.number="advance" type="range" min="0" max="40" step="1" />
        </div>

        <div class="field">
          <label for="buyout">Выкупная стоимость: <strong>{{ buyout.toFixed(1) }}%</strong></label>
          <input id="buyout" v-model.number="buyout" type="range" min="0.1" max="25" step="0.1" />
          <p class="field-hint">Минимальная выкупная стоимость — от 0,1% стоимости предмета лизинга.</p>
        </div>

        <div class="field">
          <label for="term">Срок лизинга: <strong>{{ term }} месяцев</strong></label>
          <input id="term" v-model.number="term" type="range" min="12" max="72" step="6" />
        </div>

        <div class="grid grid-results calc-results">
          <div>
            <p class="field-hint">Сумма аванса</p>
            <p class="calc-result-value">{{ fmt(advanceAmount) }}</p>
          </div>
          <div>
            <p class="field-hint">Выкупная стоимость</p>
            <p class="calc-result-value">{{ fmt(buyoutAmount) }}</p>
          </div>
          <div>
            <p class="field-hint">Ориентировочный платёж в месяц</p>
            <p class="calc-result-value calc-result-value--primary">
              {{ fmt(monthlyPayment) }}
            </p>
          </div>
        </div>

        <p class="field-hint docs-note">
          Условия договора лизинга определяются по согласованию сторон.
        </p>

        <a href="#zayavka" class="btn btn-primary btn-block calc-cta">Получить точный расчёт</a>
      </div>
    </div>
  </section>
</template>
